import React from "react";
import pinnacleImg from "@/assets/images/badges/pinnacle.png";
import premierImg from "@/assets/images/badges/premier.png";
import benchmarkImg from "@/assets/images/badges/benchmark.png";
import standardImg from "@/assets/images/badges/standard.png";

const badgeConfig = {
  Pinnacle: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-400", img: pinnacleImg },
  Premier: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-400", img: premierImg },
  Benchmark: { bg: "bg-green-100", text: "text-green-800", border: "border-green-400", img: benchmarkImg },
  Standard: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-400", img: standardImg },
};

const sizeClasses = {
  xs: "px-1.5 py-0.5 text-[10px]",
  sm: "px-2 py-0.5 text-xs",
  md: "px-3 py-1 text-sm",
  lg: "px-4 py-1.5 text-base",
};

const barSizeConfig = {
  xs: { containerClass: "px-2 py-1 text-xs gap-1", scoreClass: "text-sm font-bold", labelClass: "text-[10px]" },
  sm: { containerClass: "px-2 py-0.5 text-xs gap-1", scoreClass: "text-xs font-bold", labelClass: "text-xs" },
  md: { containerClass: "px-4 py-2 text-base gap-2", scoreClass: "text-xl font-bold", labelClass: "text-base" },
  lg: { containerClass: "px-6 py-3 text-lg gap-3", scoreClass: "text-2xl font-bold", labelClass: "text-lg" },
};

const getScoreBg = (numericScore) => {
  if (numericScore >= 80) return "bg-green-200";
  if (numericScore >= 60) return "bg-yellow-200";
  if (numericScore >= 40) return "bg-orange-200";
  return "bg-red-200";
};

const circleSizeConfig = {
  xs: { radius: 35, strokeWidth: 4, containerClass: "w-12 h-12", textClass: "text-xs", labelClass: "text-[8px]", fontWeight: "font-semibold" },
  sm: { radius: 35, strokeWidth: 4, containerClass: "w-16 h-16", textClass: "text-sm", labelClass: "text-[10px]", fontWeight: "font-semibold" },
  md: { radius: 45, strokeWidth: 6, containerClass: "w-24 h-24", textClass: "text-3xl", labelClass: "text-sm", fontWeight: "font-bold" },
  lg: { radius: 60, strokeWidth: 8, containerClass: "w-32 h-32", textClass: "text-4xl", labelClass: "text-base", fontWeight: "font-bold" },
};

const getScoreColor = (numericScore) => {
  if (numericScore >= 80) return "#4ade80";
  if (numericScore >= 60) return "#facc15";
  if (numericScore >= 40) return "#fb923c";
  return "#f87171";
};

const alignClasses = {
  left: "items-start",
  center: "items-center",
  right: "items-end",
};

function TrustScore({ score = "Standard", numericScore, size = "md", variant = "circle", showBadge = false, align = "center", strokeWidth: customStrokeWidth }) {
  const badgeConf = badgeConfig[score] || badgeConfig.Standard;
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  if (variant === "bar") {
    const barConf = barSizeConfig[size] || barSizeConfig.md;
    const progress = Math.min(Math.max(numericScore || 0, 0), 100);
    const bg = getScoreBg(progress);
    const sharedPill = size === "sm" ? barConf.containerClass : null;
    return (
      <div className="inline-flex items-center gap-2">
        <span className={`inline-flex items-center rounded-full ${bg} text-gray-800 ${barConf.containerClass}`}>
          <span className={barConf.scoreClass}>{Math.round(progress)}</span>
          <span className={barConf.labelClass}>TrustScore</span>
        </span>
        {showBadge && (
          <>
            <img src={badgeConf.img} alt={score} className="h-auto w-auto object-contain" />
          </>
        )}
      </div>
    );
  }

  const circleConf = circleSizeConfig[size] || circleSizeConfig.md;
  const { radius, strokeWidth: defaultStrokeWidth, containerClass, textClass, labelClass, fontWeight } = circleConf;
  const strokeWidth = customStrokeWidth || defaultStrokeWidth;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const progress = Math.min(Math.max(numericScore || 0, 0), 100);
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const color = getScoreColor(progress);

  return (
    <div className={`flex flex-col ${alignClasses[align] || alignClasses.center} justify-center gap-1`}>
      {numericScore != null && (
        <div className={`relative ${containerClass}`}>
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={normalizedRadius} fill="transparent" stroke="#e5e7eb" strokeWidth={strokeWidth} />
            <circle cx="50" cy="50" r={normalizedRadius} fill="transparent" stroke={color} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" className="transition-all duration-500 ease-out" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
            <span className={`${textClass} ${fontWeight} text-gray-800 leading-none`}>{Math.round(numericScore)}</span>
            <span className={`${labelClass} text-gray-500 leading-none`}>Trust</span>
          </div>
        </div>
      )}

      {showBadge && (
          <>
            <img src={badgeConf.img} alt={score} className="h-auto w-auto object-contain" />
          </>
        )}
    </div>
  );
}

export default TrustScore;

/* --- ORIGINAL CIRCULAR PROGRESS IMPLEMENTATION (commented out for future revert) ---
function TrustScore({ score = 91, label = "Trust", size = "md", strokeWidth: customStrokeWidth }) {
  // Size configurations
  const sizeConfig = {
    sm: { radius: 30, strokeWidth: 4, containerClass: "w-16 h-16", textClass: "text-base", labelClass: "text-[10px]" },
    md: { radius: 45, strokeWidth: 6, containerClass: "w-24 h-24", textClass: "text-3xl", labelClass: "text-sm" },
    lg: { radius: 60, strokeWidth: 8, containerClass: "w-32 h-32", textClass: "text-4xl", labelClass: "text-base" },
  };

  const config = sizeConfig[size] || sizeConfig.md;
  const { radius, strokeWidth: defaultStrokeWidth, containerClass, textClass, labelClass } = config;
  const strokeWidth = customStrokeWidth || defaultStrokeWidth;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  // Score is out of 100, calculate the stroke offset
  const progress = Math.min(Math.max(score, 0), 100);
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Color based on score
  const getColor = (score) => {
    if (score >= 80) return "#4ade80"; // green-400
    if (score >= 60) return "#facc15"; // yellow-400
    if (score >= 40) return "#fb923c"; // orange-400
    return "#f87171"; // red-400
  };

  const color = getColor(score);

  return (
    <div className="flex flex-col items-center justify-center">
      <div className={`relative ${containerClass}`}>
        <svg
          className="w-full h-full -rotate-90"
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r={normalizedRadius}
            fill="transparent"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx="50"
            cy="50"
            r={normalizedRadius}
            fill="transparent"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className={`${textClass} font-bold text-gray-800 leading-none`}>{Math.round(score)}</span>
          <span className={`${labelClass} text-gray-500 leading-none mt-0.5`}>{label}</span>
        </div>
      </div>
    </div>
  );
}
--- END ORIGINAL IMPLEMENTATION --- */
